import React, { useState, useEffect } from "react";
import { Card, Col, Button, Form, Row, Breadcrumb, InputGroup, Badge, Table } from "react-bootstrap";
import Divider from "components/common/divider";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios"; // Import Axios
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { handleLoadingAndAccess, generateGUID, fetchRoleAndClaims } from "../../assets/js/common";

const NewWorkTask = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [selectedIndexes, setSelectedIndexes] = useState([]);

    // Validation Schema
    const schema = yup
        .object({
            task: yup.string().required(t("taskNameRequired")),
            subTasks: yup.array()
                .of(
                    yup.object().shape({
                        value: yup.string().required(t("subTasksRequired")),
                    })
                )
                .test("at-least-one-filled", t("subTasksRequired"), (value) => {
                    return Array.isArray(value) && value.some(v => v.value?.trim());
                }),

        })
        .required(); // Entire schema is required

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(schema),
        defaultValues: {
            subTasks: [{ value: "" }]
        },
    });

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "workTasks",
            claimValue: "create",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    const {
        handleSubmit,
        register,
        watch,
        control,
        formState: { errors },
        reset,
        trigger,
        setValue, clearErrors,
    } = methods;

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const fieldsWithErrors = Object.keys(errors);
            trigger(fieldsWithErrors);
        }
    }, [i18n.language]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: "subTasks"
    });

    const handleDiscard = () => {
        reset({ task: "", subTasks: [] });
        navigate("/task"); //redirect to overview table
    };

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            // 1. Generate task ID
            const taskId = await generateGUID();

            const selectedCustomerId = localStorage.getItem("customerId");

            if (!selectedCustomerId) {
              setIsLoading(false);
              console.error("CustomerId is missing");
              return;
            }
            // 2. Fetch latest index for task
            const indexRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/taskRoute/tasks/latest-index`,
              {
                params: { customerId: selectedCustomerId },
                headers: {
                    Authorization: process.env.REACT_APP_BasicAuthSecret
                }
              }
            );

            const index = (indexRes.data.latestIndex || 0) + 1;

            // 3. Prepare createdBy info
            const createdBy = localStorage.getItem("UserId") || "Unknown";
            const customerId = localStorage.getItem("customerId");

            if (!customerId) {
              console.error("CustomerId is missing");
              return;
            }

            // 4. Prepare task payload
            const taskPayload = {
                taskId,
                customerId,
                index,
                task: data.task,
                subTaskNumber: data.subTasks.length,
                createdBy,
            };

            // 5. Save task
            const taskResponse = await axios.post(`${process.env.REACT_APP_API_URL}/taskRoute/task`, taskPayload,{
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );

            if (taskResponse.status === 201) {
                // 6. Prepare subtask payloads with unique IDs
                const subTasksPayload = await Promise.all(
                    data.subTasks.map(async (subtask) => ({
                        SubtaskId: await generateGUID(),
                        TaskId: taskId,
                        Subtask: subtask.value,
                        CreatedBy: createdBy,
                    }))
                );

                // 7. Save subtasks separately
                const subtaskResponse = await axios.post(
                    `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks`,
                    { subtasks: subTasksPayload },
                    {
                        headers: {
                            Authorization: process.env.REACT_APP_BasicAuthSecret
                        }
                    }
                );

                if (subtaskResponse.status === 201) {
                    toast.success(t("taskAndSubtasksSavedSuccessMessage"));
                    handleDiscard();
                } else {
                    toast.error(t("subtaskSaveFailedMessage"));
                }
            } else {
                toast.error(t("taskSaveFailedMessage"));
            }
        } catch (error) {
            console.error("Error saving task or subtasks:", error);
            toast.error(t("saveErrorMessage"));
        }

        setIsLoading(false);
    };

    const toggleSelect = (index) => {
        setSelectedIndexes((prev) =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const removeSelectedRows = () => {
        // Remove from react-hook-form's field array
        selectedIndexes
            .sort((a, b) => b - a) // delete from end to avoid index shift
            .forEach(i => remove(i));

        // Clear selection
        setSelectedIndexes([]);
    };

    const loadingOrRedirect = handleLoadingAndAccess(
        isLoading,
        hasAccess,
    );

    if (loadingOrRedirect) {
        return loadingOrRedirect; // Render the loading spinner or handle navigation
    }

    return (
        <>
            <FormProvider {...methods}>
                <Row className="g-3 mb-3">
                    <Col lg={12} xxl={12}>
                        <Card className="mb-3 h-100">
                            <Background
                                image={corner4}
                                className="bg-card"
                                style={{
                                    borderTopRightRadius: "0.375rem",
                                    borderBottomRightRadius: "0.375rem",
                                }}
                            />
                            <Card.Body className="position-relative pe-4 pb-2">
                                <Row className="flex-between-center">
                                    <Col md>
                                        <h5 className="mb-2 mb-md-0"> {t("newTask")}</h5>
                                        <Breadcrumb>
                                            <Breadcrumb.Item
                                                as={Link}
                                                to="/task"
                                                className="fw-semi-bold mb-1 fs-9 data-font-colour"
                                                active
                                            >
                                                {t("task")}
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item className="fw-bold mb-1 fs-9 data-font-colour" active>
                                                {t("new")}
                                            </Breadcrumb.Item>
                                        </Breadcrumb>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {!isLoading ?
                    (<Form onSubmit={handleSubmit(onSubmit)}>
                        <Card className="mb-3">
                            {/* Werkgegevens Section */}
                            <Card.Header as="h5" className="bg-body-tertiary data-font-colour">
                                {t("taskDetails")}
                            </Card.Header>

                            <Card.Body>
                                {/* Row with Task and Subtask input fields */}
                                <Row>
                                    {/* Task Name Input */}
                                    <Col md="2" className="mb-2 data-font-size data-font-colour">
                                        <Form.Label className="data-font-size">
                                            {t("taskName")}<span className="text-danger">*</span>
                                        </Form.Label>
                                    </Col>
                                    <Col md="4" className="mb-2 data-font-size data-font-colour">
                                        <Form.Group>
                                            <Form.Control
                                                type="text"
                                                size="md"
                                                {...register("task")}
                                                isInvalid={!!errors.task}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.task?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mt-2">
                                            <Table hover id="subtaskTable" className="table table-bordered table-sm">
                                                <thead>
                                                    <tr>
                                                        <th
                                                            className="checkbox-header text-center"
                                                            onClick={() => {
                                                                if (selectedIndexes.length === fields.length) {
                                                                    setSelectedIndexes([]);
                                                                } else {
                                                                    setSelectedIndexes(fields.map((_, idx) => idx));
                                                                }
                                                            }}
                                                        >
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={selectedIndexes.length === fields.length && fields.length > 0}
                                                                disabled={fields.length === 0}
                                                                className="checkbox-input"
                                                                onClick={(e) => {
                                                                    // Allow checkbox click to also trigger the selection
                                                                    e.stopPropagation(); // Prevent bubbling if needed
                                                                    if (selectedIndexes.length === fields.length) {
                                                                        setSelectedIndexes([]);
                                                                    } else {
                                                                        setSelectedIndexes(fields.map((_, idx) => idx));
                                                                    }
                                                                }}
                                                            />
                                                        </th>

                                                        <th className="text-center" style={{ position: "relative" }}>
                                                            {t("subtask")}

                                                            {/* Add & Delete Buttons aligned right in header cell */}
                                                            <div className="float-end">
                                                                {/* <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={async () => {
                                                                        const currentSubtasks = watch("subTasks");

                                                                        if (currentSubtasks.length === 0) {
                                                                            append({ value: "" });
                                                                            return;
                                                                        }

                                                                        const isValid = await trigger("subTasks"); // validate all subtasks
                                                                        const lastSubtask = currentSubtasks[currentSubtasks.length - 1];

                                                                        if (!isValid || !lastSubtask?.value?.trim()) {
                                                                            return; // Don't add new row if validation fails
                                                                        }

                                                                        append({ value: "" });
                                                                    }}

                                                                    title={t("addSubtask")}
                                                                    className="me-2"
                                                                >
                                                                    <i className="fa fa-plus" />
                                                                </Button> */}
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={async () => {
                                                                        const sorted = [...selectedIndexes].sort((a, b) => b - a);
                                                                        const remaining = fields.length - sorted.length;

                                                                        if (remaining === 0) {
                                                                            // Remove one and keep one empty row
                                                                            remove(sorted[0]);
                                                                            setValue("subTasks.0.value", "", { shouldValidate: true, shouldDirty: true });

                                                                            // Optionally ensure entire field array gets validated
                                                                            await trigger("subTasks");
                                                                        } else {
                                                                            sorted.forEach((index) => remove(index));
                                                                            await trigger("subTasks");
                                                                        }

                                                                        setSelectedIndexes([]);
                                                                    }}

                                                                    disabled={selectedIndexes.length === 0}
                                                                    title={t("deleteSelected")}
                                                                >
                                                                    <i className="fa fa-trash" />
                                                                </Button>


                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((field, index) => (
                                                        <tr key={field.id}>
                                                            <td
                                                                className="checkbox-cell text-center"
                                                                onClick={() => toggleSelect(index)}
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={selectedIndexes.includes(index)}
                                                                    readOnly
                                                                    disabled={fields.length === 0}
                                                                    className="checkbox-input"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleSelect(index); // So checkbox click also triggers selection
                                                                    }}
                                                                />
                                                            </td>

                                                            <td>
                                                                <Form.Control
                                                                    type="text"
                                                                    {...register(`subTasks.${index}.value`)}
                                                                    isInvalid={!!errors.subTasks?.[index]?.value}
                                                                    onKeyDown={async (e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.preventDefault(); // prevent form submission
                                                                            const isValid = await trigger(`subTasks.${index}.value`);
                                                                            const currentValue = watch(`subTasks.${index}.value`);

                                                                            if (isValid && currentValue.trim()) {
                                                                                append({ value: "" });
                                                                            }
                                                                        }
                                                                    }}
                                                                />

                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.subTasks?.[index]?.value?.message}
                                                                </Form.Control.Feedback>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            <div className="d-flex mb-3">
                                                {/* <Button variant="success" onClick={handleAddSubtask}>
                                                  + {t("addSubtask")}
                                                </Button> */}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={async () => {
                                                        const currentSubtasks = watch("subTasks");

                                                        // Ensure at least one row exists
                                                        if (currentSubtasks.length === 0) {
                                                            append({ value: "" });
                                                            return;
                                                        }

                                                        const isValid = await trigger("subTasks"); // validate all subtasks
                                                        const lastSubtask = currentSubtasks[currentSubtasks.length - 1];

                                                        if (!isValid || !lastSubtask?.value?.trim()) {
                                                            return; // Don't add new row if validation fails
                                                        }

                                                        append({ value: "" });
                                                    }}

                                                    title={t("addSubtask")}
                                                    className="me-2"
                                                >
                                                    <i className="fa fa-plus" /> {t("addSubtask")}
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>

                            {/* Action */}
                            <Divider></Divider>
                            <Card.Body className="pt-0">
                                <Row className="justify-content-end">
                                    <Col xs="auto">
                                        <Button
                                            variant="falcon-default"
                                            size="sm"
                                            className="me-3"
                                            type="button"
                                            onClick={() => handleDiscard()}
                                        >
                                            {t("cancel")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? t("saving") : t("save")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Form>) : <div className="text-center my-5">
                        <span >{t("importRelationships")}</span>
                        <Spinner animation="border" role="status">
                        </Spinner>
                    </div>}
            </FormProvider>
        </>
    );
};

export default NewWorkTask;